import React from "react";
import {
  Sparkles,
  Layers,
  SprayCan,
  Plug,
  BatteryCharging,
  Bolt,
  Truck,
  Wrench,
  Snowflake,
  Droplet,
  FlaskConical,
  Lightbulb,
  Droplets,
  Cog,
  Forklift,
  Settings2,
  Fan,
  Briefcase,
  Trees,
  Package,
  PaintBucket,
  Pipette,
  ShowerHead,
  Wind,
  CircleDot,
  Waves,
  Boxes,
  HardHat,
  Lock,
  Gauge,
  Hammer,
  Flame,
} from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const CATEGORIES = [
  { icon: Sparkles, name: "Abrasives" },
  { icon: Layers, name: "Adhesives, Sealants & Tape" },
  { icon: SprayCan, name: "Cleaning & Janitorial" },
  { icon: Plug, name: "Electrical" },
  { icon: BatteryCharging, name: "Electronics & Batteries" },
  { icon: Bolt, name: "Fasteners" },
  { icon: Truck, name: "Fleet & Vehicle Maintenance" },
  { icon: Wrench, name: "Hardware" },
  { icon: Snowflake, name: "HVAC & Refrigeration" },
  { icon: Droplet, name: "Hydraulics" },
  { icon: FlaskConical, name: "Lab Supplies" },
  { icon: Lightbulb, name: "Lighting" },
  { icon: Droplets, name: "Lubrication" },
  { icon: Cog, name: "Machining" },
  { icon: Forklift, name: "Material Handling" },
  { icon: Settings2, name: "Mechanical" },
  { icon: Fan, name: "Motors" },
  { icon: Briefcase, name: "Office Supplies" },
  { icon: Trees, name: "Outdoor Equipment" },
  { icon: Package, name: "Packaging & Shipping" },
  { icon: PaintBucket, name: "Paints, Equipment & Supplies" },
  { icon: Pipette, name: "Pipe, Hose, Tube & Fittings" },
  { icon: ShowerHead, name: "Plumbing" },
  { icon: Wind, name: "Pneumatics" },
  { icon: CircleDot, name: "Power Transmission" },
  { icon: Waves, name: "Pumps" },
  { icon: Boxes, name: "Raw Material" },
  { icon: HardHat, name: "Safety (PPE)" },
  { icon: Lock, name: "Security" },
  { icon: Gauge, name: "Test Instruments" },
  { icon: Hammer, name: "Tools" },
  { icon: Flame, name: "Welding" },
];

export default function Categories() {
  const { t } = useLocale();
  return (
    <section
      id="categories"
      data-testid="categories-section"
      className="bg-slate-50 py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
              <span className="inline-block h-px w-8 bg-slate-300" />
              <span>{t("landing.categories.eyebrow")}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05] text-slate-900">
              {t("landing.categories.headline_a")}
              <br className="hidden md:block" />
              <span className="text-slate-400">{t("landing.categories.headline_b")} </span>{t("landing.categories.headline_c")}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 text-lg leading-relaxed">
              {t("landing.categories.body")}
            </p>
          </div>
        </div>

        {/* Categories — dense editorial grid */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => (
              <div
                key={c.name}
                data-testid={`category-tile-${i}`}
                className={`group relative p-5 md:p-6 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                  // bottom border on all but last row
                  i < CATEGORIES.length - (CATEGORIES.length % 4 === 0 ? 4 : CATEGORIES.length % 4)
                    ? "border-b border-slate-100"
                    : ""
                } ${
                  // right border on non-last column (4-col)
                  (i + 1) % 4 !== 0 ? "lg:border-r lg:border-slate-100" : ""
                } ${
                  (i + 1) % 3 !== 0 ? "md:border-r md:border-slate-100 lg:border-r" : "md:border-r-0 lg:border-r"
                } ${(i + 1) % 2 !== 0 ? "border-r border-slate-100 md:border-r" : "border-r-0 md:border-r"}`}
              >
                <div className="h-10 w-10 rounded-md bg-slate-50 border border-slate-200 text-[#0072CE] flex items-center justify-center shrink-0 group-hover:bg-[#0072CE] group-hover:text-white group-hover:border-[#0072CE] transition-colors">
                  <c.icon size={18} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display font-semibold text-slate-900 text-[15px] tracking-tight leading-tight truncate">
                    {c.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-slate-500">
          <div>
            {t("landing.categories.footnote")}
          </div>
          <div className="hidden md:block font-mono text-xs text-slate-400">
            32 / 32 categories live
          </div>
        </div>
      </div>
    </section>
  );
}
